import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Progress, Spinner } from 'reactstrap';
import { FaHistory } from 'react-icons/fa';
import Swal from 'sweetalert2';
import {
  cancelBackgroundJob,
  getActiveBackgroundJobs,
  getBackgroundJobErrors,
  getRecentBackgroundJobs,
  retryBackgroundJob,
} from '../../services/backgroundJobService';
import './GlobalJobCenter.css';

const ACTIVE = ['QUEUED', 'PENDING', 'RUNNING', 'CANCEL_REQUESTED'];
const RETRYABLE = ['FAILED', 'INTERRUPTED', 'CANCELLED'];
const labels = {
  QUEUED: 'En file', PENDING: 'En attente', RUNNING: 'En cours', CANCEL_REQUESTED: 'Annulation demandée',
  CANCELLED: 'Annulé', INTERRUPTED: 'Interrompu', COMPLETED: 'Terminé',
  COMPLETED_WITH_ERRORS: 'Terminé avec erreurs', FAILED: 'Échec',
};
const colors = {
  QUEUED: 'secondary', PENDING: 'secondary', RUNNING: 'primary', CANCEL_REQUESTED: 'warning',
  CANCELLED: 'secondary', INTERRUPTED: 'warning', COMPLETED: 'success',
  COMPLETED_WITH_ERRORS: 'warning', FAILED: 'danger',
};
const colorFor = job => job.status === 'COMPLETED' && job.failed > 0
  ? 'warning' : (colors[job.status] || 'secondary');
const labelFor = job => job.status === 'COMPLETED' && job.failed > 0
  ? 'TerminÃ© avec erreurs' : (labels[job.status] || job.status);

const parseDate = value => value ? new Date(value.replace(' ', 'T')).getTime() : null;
const duration = seconds => {
  if (seconds === null || seconds === undefined) return '—';
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  return [hours ? `${hours} h` : null, minutes ? `${minutes} min` : null, `${secs} s`]
    .filter(Boolean).join(' ');
};

const GlobalJobCenter = () => {
  const [open, setOpen] = useState(false);
  const [activeJobs, setActiveJobs] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [errors, setErrors] = useState({});
  const [, tick] = useState(0);
  const previousStatuses = useRef(new Map());
  const initialized = useRef(false);

  const notifyTransitions = useCallback(jobs => {
    const next = new Map(jobs.map(job => [job.id, job.status]));
    if (initialized.current) {
      jobs.forEach(job => {
        const before = previousStatuses.current.get(job.id);
        if (before && ACTIVE.includes(before) && !ACTIVE.includes(job.status)) {
          Swal.fire({
            toast: true, position: 'top-end', timer: 4500, showConfirmButton: false,
            icon: job.status === 'FAILED' ? 'error' : (job.status === 'COMPLETED_WITH_ERRORS' || job.failed > 0) ? 'warning' : 'success',
            title: `${job.title} — ${labelFor(job)}`,
            text: `${job.processed} élément(s) traité(s), ${job.failed} échec(s), durée ${duration(job.elapsedSeconds)}.`,
          });
        }
      });
    }
    previousStatuses.current = next;
    initialized.current = true;
  }, []);

  const refresh = useCallback(async (includeRecent = false) => {
    try {
      const activeResponse = await getActiveBackgroundJobs();
      setActiveJobs(activeResponse.data || []);
      if (includeRecent || initialized.current) {
        const recentResponse = await getRecentBackgroundJobs();
        const recent = recentResponse.data || [];
        setRecentJobs(recent);
        notifyTransitions(recent);
      } else {
        notifyTransitions(activeResponse.data || []);
      }
    } catch {
      // Authentication and temporary network errors are handled by the shared HTTP layer.
    } finally {
      setLoading(false);
    }
  }, [notifyTransitions]);

  useEffect(() => {
    setLoading(true);
    refresh(true);
    const clock = window.setInterval(() => tick(value => value + 1), 1000);
    const started = () => refresh(true);
    window.addEventListener('background-job-started', started);
    return () => {
      window.clearInterval(clock);
      window.removeEventListener('background-job-started', started);
    };
  }, [refresh]);

  useEffect(() => {
    if (!activeJobs.length) return undefined;
    const poll = window.setInterval(() => refresh(false), 4000);
    return () => window.clearInterval(poll);
  }, [activeJobs.length, refresh]);

  useEffect(() => { if (open) refresh(true); }, [open, refresh]);

  const jobs = useMemo(() => {
    const merged = new Map(recentJobs.map(job => [job.id, job]));
    activeJobs.forEach(job => merged.set(job.id, job));
    return [...merged.values()].sort((a, b) =>
      (parseDate(b.startedAt) || parseDate(b.lastHeartbeatAt) || 0)
      - (parseDate(a.startedAt) || parseDate(a.lastHeartbeatAt) || 0));
  }, [activeJobs, recentJobs]);

  const elapsed = job => {
    if (!job.startedAt) return job.elapsedSeconds;
    if (!ACTIVE.includes(job.status)) return job.elapsedSeconds;
    return Math.floor((Date.now() - parseDate(job.startedAt)) / 1000);
  };

  const act = async (job, type) => {
    if (type === 'cancel') {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: 'Arrêter ce traitement ?',
        text: 'Les éléments déjà traités resteront enregistrés. L’annulation prendra effet à la prochaine étape sûre.',
        showCancelButton: true,
        confirmButtonText: 'Demander l’annulation',
        cancelButtonText: 'Continuer le traitement',
      });
      if (!confirmation.isConfirmed) return;
    }
    setActionId(job.id);
    try {
      if (type === 'cancel') await cancelBackgroundJob(job.id);
      else await retryBackgroundJob(job.id);
      await refresh(true);
    } catch (error) {
      await Swal.fire('Opération impossible', error.response?.data?.message || 'Veuillez réessayer.', 'error');
    } finally { setActionId(null); }
  };

  const toggleErrors = async job => {
    if (expandedId === job.id) { setExpandedId(null); return; }
    setExpandedId(job.id);
    if (errors[job.id] || !job.failed) return;
    try {
      const response = await getBackgroundJobErrors(job.id);
      setErrors(previous => ({ ...previous, [job.id]: response.data?.content || [] }));
    } catch { setErrors(previous => ({ ...previous, [job.id]: [] })); }
  };

  return <>
    <Button className="global-job-trigger" color={activeJobs.length ? 'primary' : 'light'} onClick={() => setOpen(true)}>
      {activeJobs.length ? <Spinner size="sm" /> : <FaHistory />}
      <span>Traitements</span>
      {activeJobs.length > 0 && <Badge color="warning" pill>{activeJobs.length}</Badge>}
    </Button>
    {open && <button type="button" className="global-job-backdrop" aria-label="Fermer" onClick={() => setOpen(false)} />}
    <aside className={`global-job-drawer ${open ? 'open' : ''}`} aria-hidden={!open}>
      <header><div><strong>Centre des traitements</strong><small>Vous pouvez continuer à utiliser l'ERP.</small></div>
        <Button close aria-label="Fermer" onClick={() => setOpen(false)} /></header>
      <div className="global-job-list">
        {jobs.length > 0 && <div className="global-job-sections">
          <span>En cours <Badge color="primary">{jobs.filter(job => ACTIVE.includes(job.status)).length}</Badge></span>
          <span>Terminées récemment <Badge color="success">{jobs.filter(job => ['COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(job.status)).length}</Badge></span>
          <span>En erreur <Badge color="danger">{jobs.filter(job => ['FAILED', 'INTERRUPTED'].includes(job.status)).length}</Badge></span>
        </div>}
        {loading && !jobs.length && <div className="text-center p-5"><Spinner /><div className="mt-2">Chargement…</div></div>}
        {!loading && !jobs.length && <div className="global-job-empty"><FaHistory size={28} /><p>Aucun traitement récent.</p></div>}
        {jobs.map(job => <section className="global-job-card" key={job.id}>
          <div className="d-flex justify-content-between gap-2"><strong>{job.title}</strong><Badge color={colorFor(job)}>{labelFor(job)}</Badge></div>
          <div className="global-job-stage">{job.currentStage || 'Préparation'}{job.currentItem ? ` — ${job.currentItem}` : ''}</div>
          {ACTIVE.includes(job.status) && parseDate(job.lastHeartbeatAt) && Date.now() - parseDate(job.lastHeartbeatAt) > 30000 &&
            <div className="small text-warning mb-2">Aucune mise à jour récente; le serveur peut encore travailler.</div>}
          <Progress value={job.progressPercentage || 0} animated={ACTIVE.includes(job.status)} color={colorFor(job)}>
            {job.total ? `${Math.round(job.progressPercentage || 0)} %` : ACTIVE.includes(job.status) ? 'En cours' : ''}
          </Progress>
          <div className="global-job-stats"><span>{job.processed}/{job.total || '?' } traités</span><span>{job.failed} échec(s)</span><span>Écoulé : {duration(elapsed(job))}</span></div>
          {job.estimatedRemainingSeconds !== null && job.estimatedRemainingSeconds !== undefined && ACTIVE.includes(job.status) &&
            <small className="text-muted">Estimation restante : {duration(job.estimatedRemainingSeconds)} · {job.itemsPerSecond} élément(s)/s</small>}
          {job.errorSummary && <div className="text-danger small mt-2">{job.errorSummary}</div>}
          <div className="global-job-actions">
            {ACTIVE.includes(job.status) && <Button size="sm" color="outline-danger" disabled={actionId === job.id || job.status === 'CANCEL_REQUESTED'} onClick={() => act(job, 'cancel')}>{actionId === job.id ? <Spinner size="sm" /> : 'Annuler'}</Button>}
            {RETRYABLE.includes(job.status) && <Button size="sm" color="outline-primary" disabled={actionId === job.id} onClick={() => act(job, 'retry')}>Relancer</Button>}
            {job.failed > 0 && <Button size="sm" color="link" onClick={() => toggleErrors(job)}>{expandedId === job.id ? 'Masquer' : 'Voir les erreurs'}</Button>}
          </div>
          {expandedId === job.id && <div className="global-job-errors">{(errors[job.id] || []).map(item => <div key={item.id}><strong>{item.sku || item.name || `#${item.productId}`}</strong> — {item.message}</div>)}{errors[job.id]?.length === 0 && <div>Aucun détail disponible.</div>}</div>}
        </section>)}
      </div>
    </aside>
  </>;
};

export default GlobalJobCenter;
